/** OData string literals escape a single quote by doubling it. */
export const escapeReplayODataString = (value: string): string =>
  value.replace(/'/g, "''");

/**
 * Contains-match filter for a typed user query (first, last, email). Multi-word
 * queries AND the per-token clauses so "sarah v" does not match every Sarah.
 */
export const buildReplayUserContainsFilter = (query: string): string => {
  const tokens = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  return tokens
    .map((term) => {
      const t = escapeReplayODataString(term);
      return (
        `(contains(tolower(firstName),'${t}')` +
        ` or contains(tolower(lastName),'${t}')` +
        ` or contains(tolower(email),'${t}'))`
      );
    })
    .join(' and ');
};

/** Exact match on first or last name for a single typed token (#31323). */
export const buildReplayUserExactTokenFilter = (
  token: string
): string | null => {
  const normalized = token.trim().toLowerCase();
  if (!normalized || normalized.includes(' ')) {
    return null;
  }
  const t = escapeReplayODataString(normalized);
  return `tolower(firstName) eq '${t}' or tolower(lastName) eq '${t}'`;
};

/** Exact first+last pair filter (both name orders). */
export const buildReplayUserExactNameFilter = (
  query: string
): string | null => {
  const tokens = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  if (tokens.length !== 2) {
    return null;
  }
  const [a, b] = tokens.map(escapeReplayODataString);
  return (
    `(tolower(firstName) eq '${a}' and tolower(lastName) eq '${b}')` +
    ` or (tolower(firstName) eq '${b}' and tolower(lastName) eq '${a}')`
  );
};

export const buildReplayUserEmailFilter = (email: string): string => {
  const normalized = email.trim().toLowerCase();
  return `tolower(email) eq '${escapeReplayODataString(normalized)}'`;
};

export const buildReplayUserEmailContainsFilter = (email: string): string => {
  const normalized = email.trim().toLowerCase();
  return `contains(tolower(email),'${escapeReplayODataString(normalized)}')`;
};

export const formatReplayUserSuggestionLabel = (input: {
  name: string;
  email?: string;
}): string => {
  const name = input.name.trim();
  const email = input.email?.trim();
  if (email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} · ${email}`;
  }
  return name || email || '';
};

/** Name portion of a suggestion row label (before the email suffix). */
export const replayUserSuggestionDisplayName = (label: string): string =>
  label.split(' · ')[0]?.trim() ?? label.trim();

/** Compact label on the User filter chip (#31323). */
export const formatReplayUserChipLabel = (
  query: string,
  selectedDisplayName = ''
): string => {
  const picked = selectedDisplayName.trim();
  if (picked) {
    return picked;
  }
  const q = query.trim();
  if (!q) {
    return 'Any user';
  }
  return q;
};

/** Value stored in the filter box after picking a suggestion — prefer name. */
export const replayUserSuggestionQueryValue = (input: {
  name: string;
  email?: string;
}): string => {
  const name = input.name.trim();
  const email = input.email?.trim();
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return name;
  }
  return email ?? name;
};
